import { Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { RETURN_URL_PARAM } from '../constants/session-lifecycle.config';

/**
 * Single choke point for every guard that bounces a signed-out (or
 * insufficiently-privileged) visitor to /login. Before this existed,
 * AdminRoleGuard and AuthGuard called `router.navigate(['/login'])` with no
 * queryParams, so a signed-out admin who clicked a deep-linked email URL
 * landed on a bare login form and then the generic post-login homepage —
 * never back at the record the link actually pointed to (Film Lapland
 * client report, idSite 584, 2026-09-03; verified fleet-wide, not
 * site-specific).
 *
 * login.component.ts already reads `?returnUrl=` and honors it
 * (RETURN_URL_PARAM, see its ngOnInit + onLoginSuccess) — this helper is
 * the write side of that existing contract, not a new one. Every guard
 * that redirects to /login MUST go through this function rather than
 * calling `router.navigate`/`createUrlTree(['/login'])` directly, so a
 * future guard can't reintroduce the same class of bug.
 *
 * Returns a UrlTree (the idiomatic Angular guard style already used by
 * DirectoryFeatureGuard / TaxFeatureGuard / SitePasswordGuard / etc. in
 * this same folder) rather than navigating imperatively, so callers just
 * `return redirectToLogin(this.router, state);`.
 */
export function redirectToLogin(router: Router, state: RouterStateSnapshot): UrlTree {
  return router.createUrlTree(['/login'], {
    queryParams: { [RETURN_URL_PARAM]: sanitizeReturnUrl(state.url) },
  });
}

/**
 * Mirrors login.component's own `sanitizeReturnUrl`: only a same-origin
 * relative path is ever honored — never empty, never "//" (browsers treat
 * that as protocol-relative to another host), never an absolute URL — so a
 * crafted destination can't become an open redirect. A `null` value here
 * is dropped from the resulting query string entirely (Angular's
 * `createUrlTree` omits any queryParams key whose value is `null`), which
 * is also how we avoid ever sending someone to /login?returnUrl=/login:
 * idle.service.ts hit exactly that self-nesting loop in production (605
 * occurrences across two tenants in 6h, 2026-07-29) before it added this
 * same guard against carrying a login URL as the destination.
 */
function sanitizeReturnUrl(url: string): string | null {
  if (!url || !url.startsWith('/') || url.startsWith('//')) return null;
  const path = url.split('?')[0].split('#')[0];
  if (path === '/login' || path.endsWith('/login')) return null;
  return url;
}
